import { useState } from "react";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUserMe } from "@/lib/user-me";
import { useDisableTwoFactor } from "@/lib/auth-me";
import { useToast } from "@/components/v2/Toast";
import { ChangePasswordDialog } from "@/components/v2/account/ChangePasswordDialog";
import { TwoFactorSetupDialog } from "@/components/v2/account/TwoFactorSetupDialog";
import { RecoveryCodesDialog } from "@/components/v2/account/RecoveryCodesDialog";
import { SettingsSection } from "@/components/v2/account/SettingsSection";

/**
 * `/account/security` — password + two-factor authentication +
 * recovery codes. Combined into one leaf because they're all the
 * "how do you prove you're you" surface; splitting them into
 * separate routes would feel pedantic.
 */
export function AccountSecurity() {
  const me = useUserMe();
  const disable = useDisableTwoFactor();
  const toast = useToast();

  const [passwordDialog, setPasswordDialog] = useState(false);
  const [twoFactorDialog, setTwoFactorDialog] = useState(false);
  const [recoveryDialog, setRecoveryDialog] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState<string | null>(null);

  const enabled = !!me.data?.mfaEnabled;

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Password"
        description="At least 12 characters. We'll sign out other devices when you change it."
      >
        <Button onClick={() => setPasswordDialog(true)}>
          Change password
        </Button>
      </SettingsSection>

      <SettingsSection
        title="Two-factor authentication"
        description="An extra step at sign-in using an authenticator app like Google Authenticator or 1Password."
      >
        {!enabled ? (
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-sm font-medium">Disabled</p>
              <p className="text-xs text-muted-foreground">
                Anyone with your password can sign in. Recommended on for
                owners and admins.
              </p>
            </div>
            <Button onClick={() => setTwoFactorDialog(true)}>Set up 2FA</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-status-completed" />
              <div className="flex-1">
                <p className="text-sm font-medium">Enabled</p>
                <p className="text-xs text-muted-foreground">
                  You'll need a code from your authenticator app every time
                  you sign in.
                </p>
              </div>
              <Button variant="outline" onClick={() => setRecoveryDialog(true)}>
                Regenerate recovery codes
              </Button>
              <Button
                variant="ghost"
                className="text-destructive hover:bg-destructive/10"
                onClick={() => setShowDisable((v) => !v)}
              >
                Disable
              </Button>
            </div>

            {showDisable && (
              <div className="rounded-md border bg-muted/30 px-3 py-3 space-y-2">
                <Label htmlFor="disable-pwd" className="text-xs">
                  Confirm with your password
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="disable-pwd"
                    type="password"
                    autoComplete="current-password"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={!pwd || disable.isPending}
                    onClick={async () => {
                      setError(null);
                      try {
                        await disable.mutateAsync(pwd);
                        toast.success("Two-factor disabled");
                        setShowDisable(false);
                        setPwd("");
                      } catch (e) {
                        const msg = (e as Error).message;
                        setError(
                          msg === "invalid_credentials"
                            ? "Password is incorrect."
                            : msg || "Couldn't disable 2FA.",
                        );
                      }
                    }}
                  >
                    {disable.isPending ? "Disabling…" : "Disable 2FA"}
                  </Button>
                </div>
                {error && <p className="text-xs text-destructive">{error}</p>}
              </div>
            )}
          </div>
        )}
      </SettingsSection>

      <ChangePasswordDialog
        open={passwordDialog}
        onClose={() => setPasswordDialog(false)}
      />
      <TwoFactorSetupDialog
        open={twoFactorDialog}
        onClose={() => setTwoFactorDialog(false)}
      />
      <RecoveryCodesDialog
        open={recoveryDialog}
        onClose={() => setRecoveryDialog(false)}
      />
    </div>
  );
}
